import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Loader2, CreditCard, CheckCircle2, XCircle, Zap } from "lucide-react";
import { toast } from "sonner";
import { StripeSetupGuide } from "./StripeSetupGuide";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

/** Stripe key management. Keys are stored in the shared database
 * (settings.stripe_config) so they survive redeploys and take effect
 * everywhere within ~30s — no redeploy needed. */
export const StripeSection = () => {
  const [config, setConfig] = useState(null);
  const [diag, setDiag] = useState(null);
  const [loadingDiag, setLoadingDiag] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ secret_key: "", publishable_key: "", webhook_secret: "" });

  const fetchStatus = useCallback(async () => {
    setLoadingDiag(true);
    try {
      const [cfgRes, diagRes] = await Promise.all([
        axios.get(`${API}/api/billing/stripe-config`, { headers: authHeader() }),
        axios.get(`${API}/api/billing/stripe-diag`),
      ]);
      setConfig(cfgRes.data);
      setDiag(diagRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Stripe config");
    } finally {
      setLoadingDiag(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.secret_key.trim() && !form.publishable_key.trim() && !form.webhook_secret.trim()) {
      toast.error("Enter at least one key to save");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/billing/stripe-config`, form, { headers: authHeader() });
      toast.success("Stripe keys verified & saved — effective immediately, no redeploy needed");
      setForm({ secret_key: "", publishable_key: "", webhook_secret: "" });
      await fetchStatus();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const ok = diag?.error == null && diag?.stripe_api_reachable;

  return (
    <Card className="p-5" data-testid="stripe-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-brand-navy" />
          <h3 className="font-semibold text-brand-navy">Stripe</h3>
        </div>
        <div className="flex items-center gap-2">
          <StripeSetupGuide />
          <Button
            size="sm"
            onClick={fetchStatus}
            disabled={loadingDiag}
            data-testid="stripe-test-connection-btn"
            className={ok ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-brand-navy hover:bg-brand-navy-hover text-white"}
          >
            {loadingDiag ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            <span className="ml-1.5">Test Connection</span>
          </Button>
        </div>
      </div>

      {diag && (
        <div className="flex items-start gap-3 mb-5 p-3 rounded-md bg-neutral-50 border" data-testid="stripe-status-summary">
          {ok ? (
            <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
          ) : (
            <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
          )}
          <div className="text-sm space-y-0.5">
            <p className="font-medium">{ok ? "Connected — key is valid" : "Connection problem"}</p>
            {diag.error && <p className="text-red-600" data-testid="stripe-status-error">{diag.error}</p>}
            <p className="text-muted-foreground">
              Mode: <span className="font-mono">{diag.stripe_key_mode || "not set"}</span>
              {" · "}Source: <span className="font-mono">{diag.key_source === "database" ? "database (admin UI)" : "env vars"}</span>
              {diag.latency_ms != null && <>{" · "}{diag.latency_ms}ms</>}
            </p>
            {config && (
              <p className="text-muted-foreground font-mono text-xs">
                Secret: {config.secret_key_masked || "not set"} · Publishable: {config.publishable_key_masked || "not set"} · Webhook: {config.webhook_secret_masked || "not set"}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="text-sm text-muted-foreground mb-4 space-y-1">
        <p className="font-medium text-foreground">How to get your keys:</p>
        <ol className="list-decimal list-inside space-y-0.5">
          <li>Log in to your Stripe Dashboard</li>
          <li>Go to <span className="font-mono">Developers → API keys</span></li>
          <li>Copy the <strong>Secret key</strong> (sk_live_...) and <strong>Publishable key</strong> (pk_live_...)</li>
          <li>Webhook Secret: <span className="font-mono">Developers → Webhooks</span> → select your endpoint → copy the <strong>Signing secret</strong> (whsec_...)</li>
        </ol>
        <p>Keys are verified before saving; blank fields keep their current value. Changes apply <strong>immediately — no redeploy needed</strong>.</p>
      </div>
      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <Label htmlFor="stripe-sk">Secret Key (sk_live_...)</Label>
          <Input
            id="stripe-sk"
            data-testid="stripe-secret-key-input"
            type="password"
            autoComplete="off"
            placeholder="sk_live_••••••••"
            value={form.secret_key}
            onChange={(e) => setForm((f) => ({ ...f, secret_key: e.target.value }))}
          />
        </div>
        <div>
          <Label htmlFor="stripe-pk">Publishable Key (pk_live_...)</Label>
          <Input
            id="stripe-pk"
            data-testid="stripe-publishable-key-input"
            autoComplete="off"
            placeholder="pk_live_••••••••"
            value={form.publishable_key}
            onChange={(e) => setForm((f) => ({ ...f, publishable_key: e.target.value }))}
          />
        </div>
        <div>
          <Label htmlFor="stripe-whsec">Webhook Secret (whsec_..., optional)</Label>
          <Input
            id="stripe-whsec"
            data-testid="stripe-webhook-secret-input"
            type="password"
            autoComplete="off"
            placeholder="whsec_••••••••"
            value={form.webhook_secret}
            onChange={(e) => setForm((f) => ({ ...f, webhook_secret: e.target.value }))}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Webhook endpoint: <span className="font-mono">{API}/api/billing/webhook</span> (Stripe Dashboard → Developers → Webhooks)
          </p>
        </div>
        <Button type="submit" disabled={saving} data-testid="stripe-save-config-btn">
          {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          Verify & Save
        </Button>
      </form>
    </Card>
  );
};
